import { cn } from '../lib/utils';
import React, { useState, useEffect, useCallback, useRef } from 'react';
import api from '../api/axios';
import { useTheme } from '../context/ThemeContext';
import DataTableToolbar from '../components/common/DataTableToolbar';
import DataTablePagination from '../components/common/DataTablePagination';
import Filters from '../components/features/submissions/Filters';
import LevyTable from '../components/features/levy/LevyTable';
import { StatCard } from '../components/common/StatCard';
import { downloadBlobFile } from '../utils/fileHelper';
import { ReasonFilterDropdown } from '../components/common/ReasonFilterDropdown';

const LevyAutomations = () => {
    const { isDark } = useTheme();
    const [data, setData] = useState([]);
    const [stats, setStats] = useState({});
    const [loading, setLoading] = useState(true);
    const [page, setPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [limit, setLimit] = useState(10);
    const [search, setSearch] = useState('');
    const [planSearch, setPlanSearch] = useState('');
    const [statusFilter, setStatusFilter] = useState('');
    const [selectedReason, setSelectedReason] = useState('all');
    const [dateRange, setDateRange] = useState({ startDate: null, endDate: null });

    const searchInputRef = useRef(null);
    const shouldFocusSearch = useRef(false);
    const planSearchInputRef = useRef(null);
    const shouldFocusPlanSearch = useRef(false);

    const handleReasonChange = (reasonCode) => {
        setSelectedReason(reasonCode);
        setPage(1);
    };

    const fetchData = useCallback(async () => {
        setLoading(true);
        try {
            const params = new URLSearchParams({
                page,
                limit,
                ...(search && { search }),
                ...(planSearch && { planSearch }),
                ...(statusFilter && { status: statusFilter }),
                ...(selectedReason !== 'all' && { reason: selectedReason })
            });

            if (dateRange.startDate) params.append('startDate', dateRange.startDate.toISOString());
            if (dateRange.endDate) params.append('endDate', dateRange.endDate.toISOString());

            const response = await api.get(`/api/levy-automations?${params}`);

            if (response.data.success) {
                setData(response.data.data);
                setTotalPages(response.data.pagination.totalPages);
            }
        } catch (error) {
            console.error('Error fetching levy automations:', error);
        } finally {
            setLoading(false);
            if (shouldFocusSearch.current && searchInputRef.current) {
                searchInputRef.current.focus();
                shouldFocusSearch.current = false;
            }
            if (shouldFocusPlanSearch.current && planSearchInputRef.current) {
                planSearchInputRef.current.focus();
                shouldFocusPlanSearch.current = false;
            }
        }
    }, [page, limit, search, planSearch, statusFilter, selectedReason, dateRange]);

    const fetchStats = useCallback(async () => {
        try {
            const response = await api.get(`/api/levy-automations/stats`);
            if (response.data.success) {
                setStats(response.data.data);
            }
        } catch (error) {
            console.error('Error fetching levy stats:', error);
        }
    }, []);

    useEffect(() => {
        fetchStats();
    }, [fetchStats]);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    const handleDateRangeChange = (start, end) => {
        setDateRange({ startDate: start, endDate: end });
        setPage(1);
    };

    const handleExportFilteredLevyAutomations = async () => {
        try {
            const params = new URLSearchParams({
                ...(search && { search }),
                ...(planSearch && { planSearch }),
                ...(statusFilter && { status: statusFilter }),
                ...(selectedReason !== 'all' && { reason: selectedReason })
            });

            if (dateRange.startDate) params.append('startDate', dateRange.startDate.toISOString());
            if (dateRange.endDate) params.append('endDate', dateRange.endDate.toISOString());

            const response = await api.get(`/api/levy-automations/export?${params}`, {
                responseType: 'blob'
            });

            downloadBlobFile(response.data, `filtered_levy_automations_${new Date().toISOString().split('T')[0]}.csv`);
        } catch (error) {
            console.error('Error exporting filtered levy automations:', error);
        }
    };

    // Filter Items definition matching new layout
    const filterItems = [
        { status: '', buttonLabel: 'All Statuses' },
        { status: 'pending_pdf', buttonLabel: 'Pending PDF' },
        { status: 'ready_to_send', buttonLabel: 'Ready to Send' },
        { status: 'emailed', buttonLabel: 'Emailed' },
        { status: 'posted', buttonLabel: 'Posted' },
        { status: 'needs_review', buttonLabel: 'Needs Review' },
        { status: 'failed_permanent', buttonLabel: 'Failed' },
    ];

    return (
        <div className={cn(
            "min-h-screen px-[16px] sm:px-[24px] py-[20px] sm:py-[30px] flex flex-col gap-[20px] sm:gap-[30px] transition-colors duration-200",
            isDark ? 'bg-[#000000]' : 'bg-[#F5F5F5]'
        )}>
            {/* Header Section */}
            <div className='flex flex-col sm:flex-row gap-[16px] sm:gap-[30px] items-start sm:items-center sm:justify-between'>
                <div className={cn("flex-1 flex flex-col gap-[6px]")}>
                    <h1
                        className={cn(
                            "font-semibold text-[22px] sm:text-[24px] leading-tight m-0 transition-colors duration-200 flex items-center gap-[8px]",
                            isDark ? 'text-white' : 'text-[#072837]'
                        )}
                    >
                        Levy Automations
                    </h1>
                    <p
                        className={cn(
                            "font-normal text-[14px] sm:text-[16px] leading-normal m-0 transition-colors duration-200",
                            isDark ? 'text-[#b0b0b0]' : 'text-[#686868]'
                        )}
                    >
                        Monitor and track levy notice deliveries
                    </p>
                </div>
                <div className={cn("flex items-center gap-[12px]")}>
                    <button
                        onClick={handleExportFilteredLevyAutomations}
                        className={cn(
                            "px-[16px] py-[12px] rounded-[12px] flex items-center gap-[6px] whitespace-nowrap font-semibold text-[16px] transition-colors duration-200 border-0",
                            isDark
                                ? 'bg-white text-[#000000] hover:!bg-[#cd6d2c]'
                                : 'bg-[#072837] text-white hover:!bg-[#cd6d2c]'
                        )}
                    >
                        <div
                            className={cn("w-[16px] h-[16px]")}
                            style={{
                                backgroundColor: isDark ? '#000000' : '#ffffff',
                                WebkitMaskImage: "url('/assets/icons/export.svg')",
                                WebkitMaskRepeat: 'no-repeat',
                                WebkitMaskSize: 'contain',
                                WebkitMaskPosition: 'center',
                                maskImage: "url('/assets/icons/export.svg')",
                                maskRepeat: 'no-repeat',
                                maskSize: 'contain',
                                maskPosition: 'center'
                            }}
                            aria-hidden="true"
                        />
                        Export Filtered Levy Automations
                    </button>
                </div>
            </div>

            {/* Stats Overview */}
            <div className={cn("grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-[16px]")}>
                <StatCard 
                    details="Processing"
                    totalBranches={(stats['pending_pdf'] || 0) + (stats['ready_to_send'] || 0) + (stats['sending_email'] || 0) + (stats['sending_post'] || 0)}
                    AnalyticsIcon="analytics"
                    isDark={isDark}
                />
                <StatCard 
                    details="Needs Review"
                    totalBranches={stats['needs_review'] || 0}
                    AnalyticsIcon="review"
                    isDark={isDark}
                />
                <StatCard 
                    details="Failed"
                    totalBranches={stats['failed_permanent'] || 0}
                    AnalyticsIcon="dangur"
                    isDark={isDark}
                />
                <StatCard 
                    details="Delivered"
                    totalBranches={(stats['emailed'] || 0) + (stats['posted'] || 0)}
                    AnalyticsIcon="success"
                    isDark={isDark}
                />
            </div>

            {/* Filters Section */}
            <Filters
                items={filterItems}
                activeStatus={statusFilter}
                onStatusChange={(status) => {
                    setStatusFilter(status);
                    setPage(1);
                }}
                viewMode=""
                isDark={isDark}
            >
                <DataTableToolbar
                    searchValue={search}
                    onSearchChange={(query) => {
                        setSearch(query);
                        setPage(1);
                        if (query.length > 0) { shouldFocusSearch.current = true; }
                    }}
                    planSearchValue={planSearch}
                    onPlanSearchChange={(query) => {
                        setPlanSearch(query);
                        setPage(1);
                        if (query.length > 0) { shouldFocusPlanSearch.current = true; }
                    }}
                    dateRange={dateRange}
                    onDateRangeChange={handleDateRangeChange}
                    searchRef={searchInputRef}
                    planSearchRef={planSearchInputRef}
                    isDark={isDark}
                    searchPlaceholder="Search by lot, recipient, email, or ID..."
                    planSearchPlaceholder="Search by plan no."
                >
                    <ReasonFilterDropdown
                        selectedReason={selectedReason}
                        onReasonChange={handleReasonChange}
                        isDark={isDark}
                    />
                </DataTableToolbar>
            </Filters>

            {/* Table Section */}
            <LevyTable
                data={data}
                title="Levy Automations"
                loading={loading}
                isDark={isDark}
            />

            {/* Pagination Controls */}
            <DataTablePagination
                page={page}
                totalPages={totalPages}
                setPage={setPage}
                limit={limit}
                setLimit={setLimit}
                isDark={isDark}
            />
        </div>
    );
};

export default LevyAutomations;
